import {
  ProductCategoryEnum,
  ProductKindEnum,
  ProductTypesEnum,
} from '@iheartjane/types';
import { z } from 'zod';

import { BrandSpecialPricesSchema } from './brandSpecials';
import {
  zBrandSpecialUserSegmentIds,
  zFlavor,
  zInventoryPotency,
  zLabResult,
  zProductUnitsEnum,
  zProductWeightSpecial,
  zTerpeneEnum,
} from './productSchemas';

const ProductWeightEnum = z.enum([
  'half gram',
  'gram',
  'two gram',
  'eighth ounce',
  'quarter ounce',
  'half ounce',
  'ounce',
]);

const PriceIdEnum = z.enum([...ProductWeightEnum.options, 'each']);

export type AlgoliaPriceId = z.infer<typeof PriceIdEnum>;

const OptionalStringOrNumber = z.union([z.string(), z.number()]).nullable();
const OptionalArrayOrString = z.union([z.string(), z.array(z.string())]);

export const AlgoliaProductSchema = z.object({
  activities: z.array(z.string()).nullable(),
  aggregate_rating: z.number(),
  allow_multiple_flower_count: z.boolean(),
  amount: z.string().nullish(),
  applicable_brand_special_ids: z.array(z.number()).nullish(),
  applicable_brand_specials_included_user_segments: zBrandSpecialUserSegmentIds,
  applicable_brand_specials_excluded_user_segments: zBrandSpecialUserSegmentIds,
  applicable_bundle_special_ids: z
    .object({
      qualify: z.array(z.number()),
      discount: z.array(z.number()),
    })
    .nullish(),
  'applicable_bundle_special_ids.discount': z.array(z.number()).nullish(),
  'applicable_bundle_special_ids.qualify': z.array(z.number()).nullish(),
  applicable_bulk_special_ids: z.array(z.number()).optional().nullable(),
  applicable_cart_total_special_ids: z.array(z.number()).optional().nullable(),
  applicable_special_ids: z.array(z.number()).optional().nullable(),
  available_weights: z.array(PriceIdEnum),
  best_seller_rank: z.number().nullish(),
  brand: z.string().nullable(),
  brand_logo_url: z.string().nullish(),
  brand_special_prices: BrandSpecialPricesSchema.nullish(),
  brand_subtype: z.string().nullable(),
  bucket_price: z.number(),
  category: z.enum(ProductCategoryEnum).nullable(),
  compound_names: z.array(zTerpeneEnum).optional(),
  description: z.string(),
  discounted_price_half_gram: z.union([z.string(), z.number()]).nullable(),
  discounted_price_gram: z.union([z.string(), z.number()]).nullable(),
  discounted_price_two_gram: z.union([z.string(), z.number()]).nullable(),
  discounted_price_eighth_ounce: z.union([z.string(), z.number()]).nullable(),
  discounted_price_quarter_ounce: z.union([z.string(), z.number()]).nullable(),
  discounted_price_half_ounce: z.union([z.string(), z.number()]).nullable(),
  discounted_price_ounce: z.union([z.string(), z.number()]).nullable(),
  discounted_price_each: z.union([z.string(), z.number()]).nullable(),
  dosage: z.string().nullish(),
  feelings: z.array(z.string()),
  flavors: z.array(zFlavor),
  has_brand_discount: z.boolean().nullish(),
  image_urls: z.array(z.string()),
  inventory_potencies: z.array(zInventoryPotency).optional(),
  kind: z.enum(ProductKindEnum),
  lab_results: z.array(zLabResult),
  lowest_available_price: z
    .object({
      found: z.boolean(),
      price: z.number().nullable(),
      val: z.string().nullable(),
    })
    .nullish(),
  max_cart_quantity: z.number(),
  max_cart_quantity_half_gram: z.number(),
  max_cart_quantity_gram: z.number(),
  max_cart_quantity_two_gram: z.number(),
  max_cart_quantity_eighth_ounce: z.number(),
  max_cart_quantity_quarter_ounce: z.number(),
  max_cart_quantity_half_ounce: z.number(),
  max_cart_quantity_ounce: z.number(),
  max_cart_quantity_each: z.number(),
  name: z.string(),
  net_weight_grams: z.number().nullish(),
  objectID: z.string(),
  percent_cbd: OptionalStringOrNumber,
  percent_cbda: OptionalStringOrNumber,
  percent_tac: OptionalStringOrNumber,
  percent_thc: OptionalStringOrNumber,
  percent_thca: OptionalStringOrNumber,
  price_each: z.union([z.string(), z.number()]).nullable(),
  price_eighth_ounce: z.union([z.string(), z.number()]).nullable(),
  price_gram: z.union([z.string(), z.number()]).nullable(),
  price_half_gram: z.union([z.string(), z.number()]).nullable(),
  price_half_ounce: z.union([z.string(), z.number()]).nullable(),
  price_ounce: z.union([z.string(), z.number()]).nullable(),
  price_quarter_ounce: z.union([z.string(), z.number()]).nullable(),
  price_two_gram: z.union([z.string(), z.number()]).nullable(),
  product_brand_id: z.optional(OptionalStringOrNumber),
  product_id: z.coerce.number(),
  quantity_units: zProductUnitsEnum.nullish(),
  quantity_value: z.number().nullish(),
  review_count: z.number().nullish(),
  root_subtype: z.string().nullable(),
  root_types: z.array(OptionalArrayOrString),
  special_amount: z.string().nullable(),
  special_custom_badge: z.string().nullable(),
  special_id: z.number().nullable(),
  special_price_each: zProductWeightSpecial.nullable(),
  special_price_eighth_ounce: zProductWeightSpecial.nullable(),
  special_price_gram: zProductWeightSpecial.nullable(),
  special_price_half_gram: zProductWeightSpecial.nullable(),
  special_price_half_ounce: zProductWeightSpecial.nullable(),
  special_price_ounce: zProductWeightSpecial.nullable(),
  special_price_quarter_ounce: zProductWeightSpecial.nullable(),
  special_price_two_gram: zProductWeightSpecial.nullable(),
  special_title: z.string().nullable(),
  store_id: z.coerce.number().nullish(),
  store_notes: z.string().nullable(),
  store_specific_product: z.boolean().optional(),
  type: z.enum(ProductTypesEnum).nullable(),
  unique_slug: z.string().nullish(),
});

export type AlgoliaProduct = z.infer<typeof AlgoliaProductSchema>;

export const AlgoliaProductsSchema = z.array(AlgoliaProductSchema);

const z_AlgoliaProductKeySchema = AlgoliaProductSchema.keyof();

export type AlgoliaProductKey = z.infer<typeof z_AlgoliaProductKeySchema>;
